import { lazy, Suspense, useEffect } from 'react'
import { SignedIn, SignedOut, useUser } from '@clerk/clerk-react'
import { clearClerkUser, identifyClerkUser } from '@loop3/observability'

const InfinityTube = lazy(() =>
  import('@/components/InfinityTube').then((m) => ({ default: m.InfinityTube }))
)
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useMatch,
} from 'react-router-dom'
import {
  AppShell,
  BreadcrumbTrail,
  Toaster,
  type BreadcrumbTrailItem,
} from '@loop3/ui'
import { EnsureActiveOrg, SignInScreen } from '@loop3/clerk'
import { AppSidebar } from '@/components/app-sidebar'
import { LupeThreadProvider } from '@/components/lupe-thread-provider'
import { useClient } from '@/hooks/useClient'
import { useCompany } from '@/hooks/useCompany'
import { usePerson } from '@/hooks/usePerson'
import { useSolution } from '@/hooks/useSolution'
import {
  KNOWLEDGE_HUB_PATH,
  L3OS_SYNC_PATH,
  SOLUTIONS_LIBRARY_PATH,
  TRIPS_PATH,
} from '@/lib/routes'
import { LupePage } from './pages/LupePage'
import { DistributionPage } from './pages/DistributionPage'
import { TripsIndexPage } from './pages/TripsIndexPage'
import { SolutionsLibraryPage } from './pages/SolutionsLibraryPage'
import { KnowledgeHubPage } from './pages/KnowledgeHubPage'
import { ClientsIndexPage } from './pages/ClientsIndexPage'
import { ClientDetailPage } from './pages/ClientDetailPage'
import { RepoListPage } from './pages/RepoListPage'
import { PackagesPage } from './pages/PackagesPage'
import { DistributionCategoryPage } from './pages/DistributionCategoryPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { ExplorerPage } from './pages/ExplorerPage'
import { CompanyPage } from './pages/CompanyPage'
import { PersonPage } from './pages/PersonPage'
import { SolutionPage } from './pages/SolutionPage'

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Lupe',
  [L3OS_SYNC_PATH]: 'L3OS Sync',
  '/l3os-center/workflows': 'L3OS Sync · Workflows',
  '/l3os-center/skills': 'L3OS Sync · Skills',
  '/l3os-center/configs': 'L3OS Sync · Configs',
  '/l3os-center/repos': 'L3OS Sync · Repos',
  '/l3os-center/packages': 'L3OS Sync · Packages',
  '/clients': 'Clients',
  '/explorer': 'Explorer',
  [KNOWLEDGE_HUB_PATH]: 'Knowledge Hub',
  [TRIPS_PATH]: 'Knowledge Hub · Trips',
  [SOLUTIONS_LIBRARY_PATH]: 'Knowledge Hub · Solutions',
}

/** The client crumb names the client, not its prefix. Its own component so the query mounts only there. */
function ClientTrail({ prefix }: { prefix: string }) {
  const { data } = useClient(prefix)

  return (
    <BreadcrumbTrail
      items={[
        { label: 'Clients', render: <Link to="/clients" /> },
        { label: data?.name ?? prefix },
      ]}
    />
  )
}

/** Explorer detail crumbs name the record once it loads. */
function ExplorerTrail({ name }: { name: string }) {
  return (
    <BreadcrumbTrail
      items={[
        { label: 'Explorer', render: <Link to="/explorer" /> },
        { label: name },
      ]}
    />
  )
}

function CompanyTrail({ slug }: { slug: string }) {
  const { data } = useCompany(slug)
  return <ExplorerTrail name={data?.name ?? slug} />
}

function PersonTrail({ id }: { id: string }) {
  const { data } = usePerson(id)
  return <ExplorerTrail name={data?.full_name ?? 'Person'} />
}

function SolutionTrail({ id }: { id: string }) {
  const { data } = useSolution(id)
  return <ExplorerTrail name={data?.name ?? 'Solution'} />
}

function AppShellLayout() {
  const location = useLocation()
  const clientMatch = useMatch('/clients/:prefix')
  const companyMatch = useMatch('/explorer/companies/:slug')
  const personMatch = useMatch('/explorer/people/:id')
  const solutionMatch = useMatch('/solutions/:id')
  const label = clientMatch
    ? (clientMatch.params.prefix ?? 'Client')
    : companyMatch || personMatch || solutionMatch
      ? 'Explorer'
      : (ROUTE_LABELS[location.pathname] ?? 'L3OS')

  const trail: BreadcrumbTrailItem[] = [{ label }]

  useEffect(() => {
    document.title = label === 'L3OS' ? 'L3OS' : `${label} · L3OS`
  }, [label])

  return (
    <LupeThreadProvider>
      <AppShell sidebar={<AppSidebar />}>
        <AppShell.Header
          label={
            clientMatch ? (
              <ClientTrail prefix={clientMatch.params.prefix as string} />
            ) : companyMatch ? (
              <CompanyTrail slug={companyMatch.params.slug as string} />
            ) : personMatch ? (
              <PersonTrail id={personMatch.params.id as string} />
            ) : solutionMatch ? (
              <SolutionTrail id={solutionMatch.params.id as string} />
            ) : (
              <BreadcrumbTrail items={trail} />
            )
          }
        />
        <AppShell.Main>
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </LupeThreadProvider>
  )
}

function Shell() {
  const { user } = useUser()

  useEffect(() => {
    if (user) identifyClerkUser(user)
    else clearClerkUser()
  }, [user])

  return (
    <>
      <Routes>
        <Route element={<AppShellLayout />}>
          <Route path="/" element={<LupePage />} />
          <Route path={L3OS_SYNC_PATH} element={<DistributionPage />} />
          <Route path="/l3os-center/repos" element={<RepoListPage />} />
          <Route path="/l3os-center/packages" element={<PackagesPage />} />
          <Route
            path="/l3os-center/:category"
            element={<DistributionCategoryPage />}
          />
          <Route path="/clients" element={<ClientsIndexPage />} />
          <Route path="/clients/:prefix" element={<ClientDetailPage />} />
          <Route path="/explorer" element={<ExplorerPage />} />
          <Route path="/explorer/companies/:slug" element={<CompanyPage />} />
          <Route path="/explorer/people/:id" element={<PersonPage />} />
          <Route path="/solutions/:id" element={<SolutionPage />} />
          <Route path={KNOWLEDGE_HUB_PATH} element={<KnowledgeHubPage />} />
          <Route path={TRIPS_PATH} element={<TripsIndexPage />} />
          <Route
            path={SOLUTIONS_LIBRARY_PATH}
            element={<SolutionsLibraryPage />}
          />
          <Route
            path="/tools"
            element={<Navigate to={KNOWLEDGE_HUB_PATH} replace />}
          />
          <Route
            path="/l3-wiki"
            element={<Navigate to={KNOWLEDGE_HUB_PATH} replace />}
          />
          <Route
            path="/l3-wiki/trips"
            element={<Navigate to={TRIPS_PATH} replace />}
          />
          <Route
            path="/vc-wiki"
            element={<Navigate to={KNOWLEDGE_HUB_PATH} replace />}
          />
          <Route path="/sign-in" element={<Navigate to="/" replace />} />
          <Route path="/sign-up" element={<Navigate to="/" replace />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toaster />
    </>
  )
}

function SignedOutShell() {
  useEffect(() => {
    document.title = 'Sign in · L3OS'
  }, [])

  return (
    <SignInScreen
      background={
        <Suspense fallback={null}>
          <InfinityTube
            className="h-full"
            backgroundColor="transparent"
            particleColorInner="#6b5ce7"
            particleColorOuter="#4a3db0"
            particleOpacity={0.5}
            particleBlending="normal"
            curveScale={16}
            cameraDistance={22}
            particleCount={3000}
          />
        </Suspense>
      }
    />
  )
}

export function App() {
  // Top-level SentryErrorBoundary is installed in main.tsx — no per-route
  // boundary needed here.
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <SignedIn>
          <EnsureActiveOrg>
            <Shell />
          </EnsureActiveOrg>
        </SignedIn>
        <SignedOut>
          <SignedOutShell />
        </SignedOut>
      </BrowserRouter>
    </div>
  )
}
